import { createClient } from '@supabase/supabase-js'
import { readAuthCallbackParams, type AuthCallbackParams } from '../utils/authCallback'

// Te zmienne muszą być dostarczone przez zmienne środowiskowe
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Brak konfiguracji Supabase: ustaw VITE_SUPABASE_URL i VITE_SUPABASE_ANON_KEY')
}

function isExpiredOrCorruptSession(value: string | null): boolean {
  if (!value) return false;
  try {
    // Jeśli JSON jest poprawny, nie uznajemy sesji za przeterminowaną –
    // zostawiamy Supabase możliwość odświeżenia jej przez refresh_token
    JSON.parse(value);
    return false;
  } catch {
    // Zły JSON w storage – traktuj jako uszkodzony wpis
    return true;
  }
}

// Owiń storage, aby czyścić przeterminowane/uszkodzone sesje zanim Supabase po nie sięgnie
const wrappedStorage = {
  getItem: (key: string) => {
    const value = localStorage.getItem(key);
    if (isExpiredOrCorruptSession(value)) {
      try { localStorage.removeItem(key); } catch {}
      return null;
    }
    return value;
  },
  setItem: (key: string, value: string) => {
    // Opcjonalnie: jeśli zapisujemy oczywiście wygasłe dane, pomiń
    if (isExpiredOrCorruptSession(value)) {
      try { localStorage.removeItem(key); } catch {}
      return;
    }
    localStorage.setItem(key, value);
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key);
  },
};

// Snapshot parametrów callbacku auth PRZED utworzeniem klienta.
// supabase-js usuwa `code` z URL-a zaraz po wymianie na sesję, więc później
// nie da się już odróżnić „nie było linku" od „wymiana się nie powiodła".
export const authCallbackParams: AuthCallbackParams =
  typeof window !== 'undefined'
    ? readAuthCallbackParams(window.location.href)
    : { hasCode: false, error: null, errorCode: null, errorDescription: null }

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: 'sb-2domy-auth-token',
    storage: wrappedStorage,
  },
})

export type Database = {
  public: {
    Tables: {
      family_invitations: {
        Row: {
          id: string
          family_id: string
          email: string
          role: 'parent_a' | 'parent_b' | 'child_viewer'
          token: string
          invited_by: string
          created_at: string
          expires_at: string
          status: 'pending' | 'accepted' | 'rejected'
          rejected_at: string | null
        }
        Insert: {
          id?: string
          family_id: string
          email: string
          role: 'parent_a' | 'parent_b' | 'child_viewer'
          token: string
          invited_by: string
          created_at?: string
          expires_at: string
          status?: 'pending' | 'accepted' | 'rejected'
          rejected_at?: string | null
        }
        Update: {
          id?: string
          family_id?: string
          email?: string
          role?: 'parent_a' | 'parent_b' | 'caregiver' | 'child_viewer'
          token?: string
          invited_by?: string
          created_at?: string
          expires_at?: string
          status?: 'pending' | 'accepted' | 'rejected'
          rejected_at?: string | null
        }
      }
      families: {
        Row: {
          id: string
          name: string
          home_a_name: string
          home_b_name: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          home_a_name?: string
          home_b_name?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          home_a_name?: string
          home_b_name?: string
          created_at?: string
          updated_at?: string
        }
      }
      family_members: {
        Row: {
          id: string
          family_id: string
          user_id: string
          role: 'parent_a' | 'parent_b' | 'child_viewer'
          encryption_key: string
          invited_at: string | null
          joined_at: string | null
          assigned_home: 'home_a' | 'home_b' | null
        }
        Insert: {
          id?: string
          family_id: string
          user_id: string
          role: 'parent_a' | 'parent_b' | 'child_viewer'
          encryption_key: string
          invited_at?: string | null
          joined_at?: string | null
          assigned_home?: 'home_a' | 'home_b' | null
        }
        Update: {
          id?: string
          family_id?: string
          user_id?: string
          role?: 'parent_a' | 'parent_b' | 'caregiver' | 'child_viewer'
          encryption_key_encrypted?: string
          invited_at?: string | null
          joined_at?: string | null
          assigned_home?: 'home_a' | 'home_b' | null
        }
      }
      children: {
        Row: {
          id: string
          family_id: string
          name: string
          birth_date: string | null
          created_at: string
        }
        Insert: {
          id?: string
          family_id: string
          name: string
          birth_date?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          family_id?: string
          name?: string
          birth_date?: string | null
          created_at?: string
        }
      }
      calendar_entries: {
        Row: {
          id: string
          child_id: string
          family_id: string
          date: string
          location: 'home_a' | 'home_b'
          comment: string | null
          created_by: string
          created_at: string
          updated_at: string
          is_encrypted?: boolean
        }
        Insert: {
          id?: string
          child_id: string
          family_id: string
          date: string
          location: 'home_a' | 'home_b'
          comment?: string | null
          created_by: string
          created_at?: string
          updated_at?: string
          is_encrypted?: boolean
        }
        Update: {
          id?: string
          child_id?: string
          family_id?: string
          date?: string
          location?: 'home_a' | 'home_b'
          comment?: string | null
          created_by?: string
          created_at?: string
          updated_at?: string
          is_encrypted?: boolean
        }
      }
    }
  }
} 